import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EquityTradingServiceStrategy } from './equity-trading-service-strategy.service';
import { TransactionType, OrderType, Account, OrderDuration, TradingDayInfo } from '../../models/common/trading-common.model';
import { AccountType, DeskRouteTimeZone, DeskRouteCode } from './../../models/common/trading-common.model';
import { TradingEntitlements } from './../../models/common/trading-entitlements';
import { TRADING_PLATFORM } from '../../utils/trading-constants';
@Injectable()
export class RetailEquityTradingServiceStrategy extends EquityTradingServiceStrategy {

    private static SUPPORTED_PLATFORM = TRADING_PLATFORM.RETAIL;

    support(platform: string): boolean {
        return RetailEquityTradingServiceStrategy.SUPPORTED_PLATFORM === platform;
    }

    public accountTypes(account: Account, isNonDollar: boolean, transactionType: TransactionType, isGTCOrder: boolean, symAccountType: AccountType): AccountType[] {
        let acctTypes: AccountType[] = [AccountType.CASH];
        const isMarginEligible: boolean = account.marginEligibleInd === 'Y';

        if (!!account && !!transactionType && !!symAccountType) {
            if (transactionType === TransactionType.BUY_TO_COVER || transactionType === TransactionType.SELL_FROM_PORTFOLIO || transactionType === TransactionType.SELL_ALL) {
                return [symAccountType];
            }
            return this.checkMarginEligible(acctTypes, isMarginEligible);
        } else if (!!account && !!transactionType && !symAccountType) {
            return this.getAccountTypes(transactionType, isMarginEligible);
        } else if (!!account && !transactionType && !symAccountType) {
            return this.checkMarginEligible(acctTypes, isMarginEligible);
        }
        return [AccountType.CASH];
    }

    private getAccountTypes(transactionType: TransactionType, isMarginEligible: boolean): AccountType[] {
        let acctTypes: AccountType[] = [];
        if (transactionType === TransactionType.SELL_SHORT || transactionType === TransactionType.BUY_TO_COVER || transactionType === TransactionType.SHORT_SALE_EXEMPT) {
            if (transactionType === TransactionType.SELL_SHORT) {
                return [AccountType.SHORT];
            }
            acctTypes = [AccountType.CASH, AccountType.SHORT];
            return this.checkMarginEligible(acctTypes, isMarginEligible);
        } else if (transactionType === TransactionType.BUY || transactionType === TransactionType.SELL_FROM_PORTFOLIO || transactionType === TransactionType.SELL_ALL) {
            acctTypes = [AccountType.CASH];
            return this.checkMarginEligible(acctTypes, isMarginEligible);
        }
        return [AccountType.CASH];
    }

    public checkMarginEligible(acctTypes: AccountType[], isMarginEligible: boolean): AccountType[] {
        if (isMarginEligible && acctTypes.indexOf(AccountType.MARGIN) < 0) {
            acctTypes.splice(1, 0, AccountType.MARGIN);
        }

        return acctTypes;

    }
    public getTransactionTypes(account: Account): TransactionType[] {
        const transactionTypes: TransactionType[] = [];
        transactionTypes.push(TransactionType.BUY);
        transactionTypes.push(TransactionType.SELL_FROM_PORTFOLIO);

        if (!!account && account.acctNavigationTypeCd !== AccountType.COD) {
            transactionTypes.push(TransactionType.SELL_ALL);
        }

        if (account.marginEligibleInd === 'Y') {
            transactionTypes.push(TransactionType.SELL_SHORT);
            transactionTypes.push(TransactionType.BUY_TO_COVER);
        }
        return transactionTypes;
    }

    public getOrderTypes(entitlements: TradingEntitlements,  isNonDollar: boolean, selectedSession: string, hasCodeValueExchange: boolean,
                          qty: number, dollarOrFractional: boolean, transtype: TransactionType): OrderType[] {
        const orderTypes: OrderType[] = [];

        // during extended hours, user can trade only in LIMIT order.
        if (selectedSession === 'EXTENDED') {
            orderTypes.push(OrderType.LIMIT);
            return orderTypes;
        }

        if (dollarOrFractional) {
            orderTypes.push(OrderType.MARKET);
            if ( ((transtype === TransactionType.SELL_FROM_PORTFOLIO || transtype === TransactionType.SELL_ALL) && qty >= 1)
                  || transtype === TransactionType.BUY ) {
              orderTypes.push(OrderType.LIMIT);
            }
            return orderTypes;
        }

        orderTypes.push(OrderType.MARKET);
        orderTypes.push(OrderType.LIMIT);
        orderTypes.push(OrderType.AON);
        orderTypes.push(OrderType.FOK);
        orderTypes.push(OrderType.STOP_LIMIT);
        orderTypes.push(OrderType.STOP);

        if (entitlements.isTrailingStopEnabled) {
            orderTypes.push(OrderType.TRAILING_STOP_LIMIT_DOLLAR);
            orderTypes.push(OrderType.TRAILING_STOP_LIMIT_PCT);

            orderTypes.push(OrderType.TRAILING_STOP_LIMIT_AON);
            orderTypes.push(OrderType.TRAILING_STOP_LIMIT_PCT_AON);

            orderTypes.push(OrderType.TRAILING_STOP_DOLLAR);
            orderTypes.push(OrderType.TRAILING_STOP_PCT);
        }
        return orderTypes;
    }

    public getDurations(selectedSession: string, tradingDayInfo: TradingDayInfo, isNonDollar: boolean, transactionType: TransactionType,
                         orderType?: string, ordQty?: number, dollarOrFractional?: boolean): OrderDuration[] {
        if (dollarOrFractional) {
          return [OrderDuration.DAY];
        }

        if (selectedSession === 'EXTENDED') {
            if (tradingDayInfo?.isECNPreTradingHrs) {
                return [OrderDuration.PREMKT];
            }
            if (tradingDayInfo?.isECNPostTradingHrs) {
                return [OrderDuration.AFTMKT];
            }
        }
        if (transactionType === TransactionType.SELL_SHORT) {
            return [OrderDuration.DAY];
        }
        if (orderType && orderType === OrderType.MARKET || orderType === OrderType.FOK) {
            return [OrderDuration.DAY];
        } else {
            return [OrderDuration.DAY, OrderDuration.GTC];
        }
    }
    public  getDeskRouteTimeZone(): DeskRouteTimeZone[] {
        return [];
    }
    public  getDeskCode(): DeskRouteCode[] {
        return [];
    }

    public getCodeValuesExchangeList(): Observable<any> {
        throw new Error('Code values exchange list is not supported on the retail platform');
    }

    public getCodeValuesFloorInstructions(): Observable<any> {
        throw new Error('Code values Order Instructions list is not supported on the retail platform');
    }
}
